import { baseApi } from "../api/baseApi.ts";
import type {
    PublicSearchRequest,
    PublicSearchResponse,
    SearchResult
} from "../api/types.ts";

// Extend the base API with public search endpoints
export const publicSearchApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // POST /public/search/ - Public search endpoint
        publicSearch: builder.mutation<PublicSearchResponse, PublicSearchRequest>({
            query: (data) => ({
                url: '/public/search/',
                method: 'POST',
                body: data,
                skipAuth: true, // Public endpoint, no auth needed
            }),
            // No tags needed since this is a public endpoint and doesn't affect cache
        }),
    }),
    overrideExisting: false,
});

// Utility function to transform API response to UI-friendly format
export const transformSearchResult = (response: PublicSearchResponse, query: string): SearchResult => {
    const { summary } = response;

    // Determine password strength category
    const getPasswordStrength = (score: number): 'Excellent' | 'Good' | 'Fair' | 'Poor' => {
        if (score >= 90) return 'Excellent';
        if (score >= 70) return 'Good';
        if (score >= 50) return 'Fair';
        return 'Poor';
    };

    // Format the last compromise date
    const formatLastSeen = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

            if (diffInHours < 24) {
                return `${Math.floor(diffInHours)}h ago`;
            } else if (diffInHours < 24 * 7) {
                return `${Math.floor(diffInHours / 24)}d ago`;
            } else if (diffInHours < 24 * 30) {
                return `${Math.floor(diffInHours / (24 * 7))}w ago`;
            } else {
                return date.toLocaleDateString();
            }
        } catch {
            return 'Unknown';
        }
    };

    return {
        query,
        totalInfections: summary.total_infections,
        employeesCompromised: summary.employees_compromised,
        usersCompromised: summary.users_compromised,
        exposedUrls: summary.exposed_urls,
        passwordStrengthScore: summary.password_strength_score,
        topStealerFamily: summary.top_stealer_family,
        lastCompromiseDate: summary.last_compromise_date,
        riskLevel: summary.risk_level,
        // Calculated fields
        totalCompromised: summary.employees_compromised + summary.users_compromised,
        passwordStrength: getPasswordStrength(summary.password_strength_score),
        lastSeen: formatLastSeen(summary.last_compromise_date)
    };
};

// Utility function to detect search type from query
export const detectSearchType = (query: string): 'email' | 'username' | 'domain' | 'ip' => {
    // Email pattern
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailPattern.test(query)) return 'email';

    // IP pattern (basic IPv4)
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (ipPattern.test(query)) return 'ip';

    // Domain pattern (contains dot and valid domain characters)
    const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (domainPattern.test(query) && query.includes('.')) return 'domain';

    // Default to username for everything else
    return 'username';
};

// Export hooks for usage in components
export const {
    usePublicSearchMutation
} = publicSearchApi;